import { useEffect, useState } from "react";
import {
  BundleContentTypeEnum,
  ModelBundle,
  ModelBundleSortFieldEnum,
  SortOrderEnum,
  useGetModelBundlesPaginatedLazyQuery,
} from "../../../../generated/graphql";

const PAGE_SIZE = 10;

export const useExperimentsModelScrollFetch = (
  searchKey: string,
  contentType: string,
) => {
  const [allItems, setAllItems] = useState<Partial<ModelBundle>[]>([]);

  const baseParams = {
    search: searchKey || undefined,
    filter: {
      isDraft: false,
      contentTypes: contentType
        ? [contentType as BundleContentTypeEnum]
        : undefined,
    },
    sort: {
      field: ModelBundleSortFieldEnum.Name,
      order: SortOrderEnum.Asc,
    },
    limit: PAGE_SIZE,
  };

  const [getExperimentModelList, { data, loading, error }] =
    useGetModelBundlesPaginatedLazyQuery({
      fetchPolicy: "cache-and-network",
    });
  const total = data?.getModelBundlesPaginated?.total ?? 0;

  useEffect(() => {
    getExperimentModelList({
      variables: { params: { ...baseParams, skip: 0 } },
      onCompleted: (models) => {
        setAllItems(models?.getModelBundlesPaginated?.items ?? []);
      },
    });
  }, [searchKey]);

  useEffect(() => {
    const container = document.querySelector("#experiment-models-list");
    const anchor = document.querySelector(
      "#experiment-models-list-observer-anchor",
    );
    const hasNextPage = allItems.length < total && !loading;
    if (!anchor || !container || !hasNextPage) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasNextPage) {
          getExperimentModelList({
            variables: { params: { ...baseParams, skip: allItems.length } },
            onCompleted: (models) => {
              setAllItems((prev) => [
                ...prev,
                ...(models?.getModelBundlesPaginated?.items ?? []),
              ]);
            },
          });
        }
      },
      { root: container, rootMargin: "20px", threshold: 0 },
    );
    observer.observe(anchor);

    return () => {
      observer.disconnect();
    };
  }, [allItems.length, total, loading]);

  return {
    availableModels: allItems,
    loading,
    error,
  };
};
